<!-- @format -->
<template>
    <div class="widget featured-resource-select-widget">
        <div class="widget-header">
            <h3>Featured Items</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div v-if="isLoading" class="loading-card">
            <loading-svg />
        </div>
        <div v-else class="content-wrapper" ref="wrapper">
            <p class="error-message" v-if="errorMessage">{{ errorMessage }}</p>
            <selector-table
                v-model="selectedItems"
                :item-container-title="`${selectedItems.length} out of 6 Featured Items`"
                :is-initializing="isLoading"
                :items="availableItems"
                :selected-items="selectedItems"
                :selected-item-cell-config="selectedItemCellConfig"
                :table-config="tableConfig"
                :filterOptions="filterOptions"
                @removed-item="handleRemovedItem"
                @remove-all="handleRemoveAll"
                @selected-item="handleSelectedItem"
                @query="onQuery"
                @filter-select="changeFilter($event)"
            />
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget">Back</app-button>
            </div>
            <div class="action-container">
                <app-button :is-loading="isLoading" @click="add"> Save </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { LandingPageTemplateBoardServiceMixin } from '@/mixins/board/landing-page-template-board-service-mixin';
import { SocialPostTemplateBoardServiceMixin } from '@/mixins/board/social-post-template-board-service-mixin';
import { SocialMediaPostTemplateService } from '@/scripts/services/social-media/social-media-post-template-service';

const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const SelectorTable = () => import('@/components/selector-table/selector-table');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');

export default {
    name: 'featured-resource-select-widget',

    components: {
        'app-button': AppButton,
        'selector-table': SelectorTable,
        'widget-close-svg': WidgetCloseSvg,
        'loading-svg': LoadingSvg,
    },

    mixins: [LandingPageTemplateBoardServiceMixin, SocialPostTemplateBoardServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,

            agencyAccountId: null,

            items: [],
            selectedItems: [],
            filteredItems: [],

            selectedItemCellConfig: {
                type: 'resource-name',
            },

            errorMessage: '',

            tableConfig: [
                {
                    cellWidth: '350px',
                    id: 1,
                    label: 'Name',
                    type: 'resource-name',
                    customHeaderStyles: {
                        justifyContent: 'center',
                        marginLeft: '40px',
                    },
                },
                {
                    cellWidth: '200px',
                    id: 2,
                    label: 'Type',
                    type: 'type',
                    property: 'type',
                    customHeaderStyles: {
                        justifyContent: 'center',
                        marginLeft: '10px',
                    },
                },
            ],

            search: '',
            selectedFilter: 'All',
            filterOptions: [
                'All',
                'Graphical Email',
                'Non-Graphical Email',
                'Journey',
                'Marketing Plan',
                'Collateral',
                'Collateral Build',
                'Landing Page',
                'Social Post',
            ],
        };
    },

    computed: {
        availableItems() {
            return this.filteredItems.reduce((acc, item) => {
                if (
                    !(
                        this.selectedItems.length > 0 &&
                        this.selectedItems.some(({ id }) => id === item.id)
                    )
                ) {
                    acc.push(item);
                }
                return acc;
            }, []);
        },

        initialSelectedResource() {
            return this.widget.options.initialSelectedResource;
        },
    },

    watch: {
        selectedFilter() {
            this.updateFilteredItems();
        },

        search() {
            this.updateFilteredItems();
        },
    },

    created() {
        this.isLoading = true;

        const promises = [this.getData(), this.getAccountId()];

        Promise.all(promises)
            .then(this.getSocialPosts)
            .then(() => {
                this.totalItem = this.items.length;
                this.filteredItems = this.items;
                this.isLoading = false;

                if (this.initialSelectedResource.length) {
                    this.setInitialSelected(this.initialSelectedResource);
                }
            });
    },

    methods: {
        getData() {
            return new Promise((resolve, reject) => {
                this.isLoading = true;

                var query = `/api/Agencies/${this.widget.options.agencyId}/Resources`;

                this.$api
                    .get(query)
                    .then((response) => {
                        this.items = response.data.map(this.mapItem);

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your resources.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        onQuery(options) {
            this.search = options.search;
        },

        changeFilter(filter) {
            if (!filter) {
                this.selectedFilter = 'All';
            } else {
                this.selectedFilter = filter;
            }
        },

        updateFilteredItems() {
            var filteredItems = this.items;

            if (this.search) {
                var formattedSearch = this.search.toLowerCase();
                filteredItems = filteredItems.filter((i) =>
                    i.name.toLowerCase().includes(formattedSearch),
                );
            }

            if (this.selectedFilter != 'All') {
                filteredItems = filteredItems.filter((i) => i.type === this.selectedFilter);
            }

            this.filteredItems = filteredItems;
        },

        getSocialPosts() {
            return new Promise((resolve, reject) => {
                var options = {
                    type: 'published',
                };

                SocialMediaPostTemplateService.getSharedTemplates(options, this.agencyAccountId)
                    .then((response) => {
                        response.data.data.map((i) => {
                            this.items.push({
                                name: i.Name || '',
                                resourceId: i.Id,
                                thumbnailImageUrl: i.PreviewImageUrl,
                                type: 'Social Post',
                                id: `social-post-${i.Id}`,
                            });
                        });
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'Failed to get social posts templates.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        getAccountId() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/api/Agencies/${this.widget.options.agencyId}/Account`)
                    .then((response) => {
                        this.agencyAccountId = response.data.Id;
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to retrieve account', { error });
                        reject();
                    });
            });
        },

        mapItem(item) {
            return {
                resourceId: item.Id,
                name: item.Name,
                thumbnailImageUrl: item.PreviewImageUrl,
                type: item.Type,
                id: `${item.Type.replace(/\s+/g, '-').toLowerCase()}-${item.Id}`,
                dragHovered: false,
            };
        },

        setInitialSelected() {
            this.selectedItems = [];
            this.initialSelectedResource.forEach((c) => {
                var matchedItem = this.items.find((i) => i.id === c.id && i.type === c.type);
                this.selectedItems.push(matchedItem);
            });
        },

        handleRemoveAll() {
            this.selectedItems.splice(0, this.selectedItems.length);
        },

        handleRemovedItem(item) {
            this.selectedItems = this.selectedItems.filter(({ id }) => id !== item.id);
        },

        handleSelectedItem(item) {
            this.errorMessage = '';
            if (
                this.selectedItems.length < 6 &&
                this.selectedItems.every(({ id }) => id !== item.id)
            ) {
                this.selectedItems.push(item);
            } else {
                this.$refs.wrapper.scroll(0, 0);
                this.errorMessage = 'Please limit to 6 featured items.';
            }
        },

        add() {
            EventBus.$emit('featured-item:select', this.selectedItems);
            this.closeWidget();
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.featured-resource-select-widget .saving-step {
    text-align: center;
}

.featured-resource-select-widget .content-wrapper {
    padding: 17px !important;
}

.featured-resource-select-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    border: 2px solid #cac6cb;
    color: #382153;
}

.featured-resource-select-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.featured-resource-select-widget .error-message {
    color: #f30070;
}

.featured-resource-select-widget .head-cell {
    font-size: 10px;
}

.featured-resource-select-widget .loading-card {
    display: flex;
    justify-content: space-around;
    width: 100%;
}
</style>
